import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TR, LANG_META } from '@/i18n/translations'
import { useAuth } from '@/auth/AuthContext'

const LANGS = ["en","ml","hi","ta","te"]

function LangSwitcher({ lang, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display:"flex", alignItems:"center", gap:7, padding:"5px 12px", background:"rgba(255,255,255,.15)", border:"1px solid rgba(255,255,255,.25)", borderRadius:20, cursor:"pointer", color:"white", fontSize:12, fontWeight:700 }}>
        <span style={{ fontSize:15 }}>{LANG_META[lang]?.flag}</span>
        <span>{LANG_META[lang]?.nativeName}</span>
        <span style={{ fontSize:9, opacity:.7 }}>▼</span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, background:"white", borderRadius:12, boxShadow:"0 12px 40px rgba(0,0,0,.18)", padding:8, zIndex:300, minWidth:170, border:"1.5px solid var(--border)" }}>
          <div style={{ fontSize:9, fontWeight:700, color:"var(--mist)", padding:"2px 10px 8px", letterSpacing:1 }}>INTERFACE LANGUAGE</div>
          {LANGS.map(l => (
            <button key={l} onClick={() => { onChange(l); setOpen(false) }} style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"8px 12px", background:lang===l?"var(--fog)":"transparent", border:"none", borderRadius:8, cursor:"pointer", textAlign:"left" }}>
              <span style={{ fontSize:16 }}>{LANG_META[l]?.flag}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:lang===l?700:500, color:lang===l?"var(--navy)":"var(--ink)" }}>{LANG_META[l]?.nativeName}</div>
                <div style={{ fontSize:10, color:"var(--mist)" }}>{TR["lang."+l]?.en}</div>
              </div>
              {lang===l && <span style={{ marginLeft:"auto", color:"var(--sky)", fontSize:14 }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function UserAvatar({ user, size = 28 }) {
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase()
  if (user?.avatar) {
    return <img src={user.avatar} alt={user.name} style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover' }}/>
  }
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:'linear-gradient(135deg,#EA580C,#F97316)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:900, color:'#fff', flexShrink:0, letterSpacing:.5 }}>
      {initials}
    </div>
  )
}

// ── TopBar — accepts isMobile + onMenuToggle for hamburger ────────────────────
export default function TopBar({ lang, onLangChange, isMobile, onMenuToggle }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef()

  useEffect(() => {
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const ROLE_LABEL = { superadmin:'Super Admin', admin:'Admin', teacher:'Teacher', parent:'Parent' }

  return (
    <div style={{
      background: '#0F1A2E',  /* hardcoded — immune to CSS var issues */
      height: 46,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '0 20px',
      gap: 16,
      flexShrink: 0,  /* never collapse */
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>

      {/* Hamburger — mobile only */}
      {isMobile && onMenuToggle && (
        <button onClick={onMenuToggle}
          style={{ marginRight:'auto', background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', borderRadius:8, padding:'5px 10px', cursor:'pointer', color:'white', fontSize:16, lineHeight:1 }}>
          ☰
        </button>
      )}

      <div style={{ fontSize:10, color:'rgba(255,255,255,.35)', fontWeight:700, letterSpacing:.5 }}>LANGUAGE</div>
      <LangSwitcher lang={lang} onChange={onLangChange}/>

      <div style={{ width:1, height:22, background:'rgba(255,255,255,.12)' }}/>

      {/* Avatar + dropdown */}
      <div ref={menuRef} style={{ position:'relative' }}>
        <button onClick={() => setMenuOpen(o => !o)}
          style={{ display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.18)',
                   borderRadius:22, padding:'4px 10px 4px 6px', cursor:'pointer', transition:'background .15s' }}
          onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.18)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,.1)'}>
          <UserAvatar user={user}/>
          <span style={{ fontSize:12, fontWeight:600, color:'#fff', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {user?.name?.split(' ')[0] || 'Account'}
          </span>
          <span style={{ fontSize:9, color:'rgba(255,255,255,.5)' }}>▼</span>
        </button>

        {menuOpen && (
          <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, background:'white', borderRadius:14,
                        boxShadow:'0 12px 40px rgba(0,0,0,.16)', padding:'8px', zIndex:400, minWidth:220,
                        border:'1.5px solid #E5E7EB', animation:'fadeDown .15s ease' }}>
            <style>{`@keyframes fadeDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px 12px', borderBottom:'1px solid #F3F4F6', marginBottom:4 }}>
              <UserAvatar user={user} size={36}/>
              <div style={{ overflow:'hidden' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.email}</div>
                <div style={{ fontSize:10, fontWeight:700, color:'#EA580C', marginTop:2 }}>{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
            </div>
            <button onClick={() => { setMenuOpen(false); navigate('/profile') }}
              style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'9px 12px', background:'none', border:'none',
                       borderRadius:9, cursor:'pointer', textAlign:'left', fontSize:13, color:'#374151', fontWeight:500 }}
              onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <span style={{ fontSize:16 }}>👤</span> My Profile
            </button>
            <div style={{ height:1, background:'#F3F4F6', margin:'4px 0' }}/>
            <button onClick={() => { setMenuOpen(false); logout() }}
              style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'9px 12px', background:'none', border:'none',
                       borderRadius:9, cursor:'pointer', textAlign:'left', fontSize:13, color:'#EF4444', fontWeight:600 }}
              onMouseEnter={e => e.currentTarget.style.background='#FFF1F0'}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <span style={{ fontSize:16 }}>🚪</span> Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  )
}